import { AxiosResponse } from "axios";
import { useApiOperation } from "src/api/hooks";
import { ApiResponse } from "src/api/public-types";
import {
  getPartnerMemberCustomFieldsAsync,
  getPartnerMemberExtraInfoAsync,
  getPartnerMemberListAsync,
  savePartnerMemberExtraInfoAsync,
  updatePartnerMemberCustomFieldsAsync,
} from "src/api/userAdditionalInfo/userAdditionalInfo-api";
import {
  PartnerMemberCustomFieldModel,
  PartnerMemberCustomFieldScope,
  PartnerMemberExtraInfo,
  PartnerMemberInputSaveItem,
  PartnerMemberListModel,
  PartnerMemberListParam,
} from "src/api/userAdditionalInfo/userAdditionalInfo-type";

/**
 * 이용자 부가정보 커스텀 필드 관련 API hook
 * 계약그룹 useContractGroup 의 커스텀 필드 파트를 복제 후 targetType=PARTNER_MEMBER 로 확장
 */
export const useUserAdditionalInfo = () => {
  const { executeAsync: getCustomFields } = useApiOperation(getPartnerMemberCustomFieldsAsync); // RCR11. 커스텀 필드 목록 조회
  const { executeAsync: updateCustomFields } = useApiOperation(updatePartnerMemberCustomFieldsAsync); // RCR12. 커스텀 필드 저장
  const { executeAsync: getPartnerMembers } = useApiOperation(getPartnerMemberListAsync); // RCR31. 파트너회원 목록 조회
  // 실패 처리(eCT501 전용 모달)는 상세 화면이 한다
  const { executeAsync: getExtraInfo } = useApiOperation(getPartnerMemberExtraInfoAsync, { noErrorModal: true }); // RCR32. 부가정보 상세 조회
  const { executeAsync: saveExtraInfo } = useApiOperation(savePartnerMemberExtraInfoAsync); // RCR33. 부가정보 저장

  // 파트너회원(이용자 부가정보) 목록 조회
  const getPartnerMemberList = async (
    request: PartnerMemberListParam,
  ): Promise<AxiosResponse<ApiResponse<{ content: PartnerMemberListModel[] }>>> => {
    return await getPartnerMembers(request);
  };

  // 커스텀 필드 목록 + 아이콘 출력 설정 조회 (RCR11)
  const getCustomFieldScope = async (): Promise<PartnerMemberCustomFieldScope> => {
    try {
      const { data } = await getCustomFields();
      return data.data || {};
    } catch (error) {
      console.error("이용자 부가정보 커스텀 필드 조회 실패:", error);
      return {};
    }
  };

  // 커스텀 필드 목록 조회
  const getCustomFieldList = async (): Promise<PartnerMemberCustomFieldModel[]> => {
    const { customFieldList } = await getCustomFieldScope();
    return customFieldList || [];
  };

  // 커스텀 필드 신규 등록
  // 저장 계열은 실패하면 공통 에러 모달을 띄운 뒤 에러를 던진다
  const addCustomField = async (request: PartnerMemberCustomFieldModel) => {
    return await updateCustomFields({ customFieldList: [request] });
  };

  // 커스텀 필드 수정 (노출/순서 토글 포함, 단건 또는 복수)
  const editCustomField = async (request: PartnerMemberCustomFieldModel | PartnerMemberCustomFieldModel[]) => {
    return await updateCustomFields({ customFieldList: Array.isArray(request) ? request : [request] });
  };

  // 커스텀 필드 삭제
  const deleteCustomField = async (request: PartnerMemberCustomFieldModel) => {
    return await updateCustomFields({ customFieldList: [request] });
  };

  // 아이콘 출력 여부 저장 (RCR12 - setting 만 전송)
  const saveIconVisible = async (isAppVisible: boolean) => {
    return await updateCustomFields({ setting: { isAppVisible } });
  };

  // 부가정보 상세 조회 (RCR32)
  const getPartnerMemberDetail = async (memberNo: string): Promise<PartnerMemberExtraInfo | undefined> => {
    try {
      const { data } = await getExtraInfo({ memberNo });
      return data.data;
    } catch (error) {
      console.error("이용자 부가정보 상세 조회 실패:", error);
      throw error;
    }
  };

  // 부가정보 저장 (RCR33)
  const savePartnerMemberDetail = async (memberNo: string, inputList: PartnerMemberInputSaveItem[]) => {
    return await saveExtraInfo({ memberNo, inputList });
  };

  return {
    getCustomFieldScope,
    getCustomFieldList,
    saveIconVisible,
    addCustomField,
    editCustomField,
    deleteCustomField,
    getPartnerMemberList,
    getPartnerMemberDetail,
    savePartnerMemberDetail,
  };
};
